import React from 'react'
import {
  Box,
  Text,
  VStack,
  Heading,
  Link
} from '@chakra-ui/react'
import { FiExternalLink } from 'react-icons/fi'
import {
  CURRENT_PRIVACY_VERSION,
  PRIVACY_LAST_UPDATED,
  LEGAL_ENTITY_US,
  LEGAL_ENTITY_US_ADDRESS,
} from '../../config/legalVersions'

export { CURRENT_PRIVACY_VERSION }

interface PrivacyPolicyContentProps {
  /** Use compact styling for modal display (no header, less padding) */
  compact?: boolean
}

export const PrivacyPolicyContent: React.FC<PrivacyPolicyContentProps> = ({ compact = false }) => {
  const headingSize = compact ? 'md' : 'xl'
  const sectionGap = compact ? 4 : 6
  const sectionMt = compact ? 6 : 12
  
  return (
    <Box maxW={compact ? 'full' : '4xl'} mx="auto" p={compact ? 0 : 6}>
      <VStack gap={sectionGap} align="stretch">
        {/* Header - only show in full page mode */}
        {!compact && (
          <>
            <Box textAlign="center">
              <Heading size="3xl" fontWeight="800" mb={6}>
                Privacy Policy
              </Heading>
              <Text color="gray.600" fontSize="md">
                Version {CURRENT_PRIVACY_VERSION} • Last updated: {PRIVACY_LAST_UPDATED}
              </Text>
            </Box>

            <Box height="1px" bg="gray.200" my={6} />

            {/* Table of Contents - only in full page mode */}
            <Box bg="blue.50" p={4} borderRadius="md" mb={6}>
              <Heading size="lg" fontWeight="700" mb={3} color="blue.700">Table of Contents</Heading>
              <Box as="ul" gap={1} fontSize="md" pl={4}>
                <Box as="li"><Link href="#introduction" color="blue.600">1. Introduction & Scope</Link></Box>
                <Box as="li"><Link href="#information-collected" color="blue.600">2. Information We Collect</Link></Box>
                <Box as="li"><Link href="#how-we-use" color="blue.600">3. How We Use Information</Link></Box>
                <Box as="li"><Link href="#information-sharing" color="blue.600">4. Information Sharing & Disclosure</Link></Box>
                <Box as="li"><Link href="#external-ai-integrations" color="blue.600">5. External AI Integrations (MCP)</Link></Box>
                <Box as="li"><Link href="#data-security" color="blue.600">6. Data Security</Link></Box>
                <Box as="li"><Link href="#data-retention" color="blue.600">7. Data Retention & Deletion</Link></Box>
                <Box as="li"><Link href="#your-rights" color="blue.600">8. Your Rights</Link></Box>
                <Box as="li"><Link href="#cookies-tracking" color="blue.600">9. Cookies & Tracking</Link></Box>
                <Box as="li"><Link href="#international-transfers" color="blue.600">10. International Transfers</Link></Box>
                <Box as="li"><Link href="#children-privacy" color="blue.600">11. Children's Privacy</Link></Box>
                <Box as="li"><Link href="#policy-changes" color="blue.600">12. Changes to Privacy Policy</Link></Box>
                <Box as="li"><Link href="#contact" color="blue.600">13. Contact Information</Link></Box>
              </Box>
            </Box>
          </>
        )}
        
        {/* Version info for compact mode */}
        {compact && (
          <Text color="gray.500" fontSize="sm" mb={2}>
            Version {CURRENT_PRIVACY_VERSION} • Last updated: {PRIVACY_LAST_UPDATED}
          </Text>
        )}

        {/* 1. Introduction & Scope */}
        <Box id="introduction" mt={compact ? 0 : sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>1. Introduction & Scope</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">
            LiveWello is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered wellness and genetic analysis platform.
          </Text>
          <Text mb={4} fontSize="md" lineHeight="tall">
            This policy applies to all services operated by Livewello LLC (United States) and Livewello Ltd (United Kingdom) (collectively, "Livewello", "we", "us", or "our"), including genetic data processing, AI-powered health coaching, profile sharing, and professional features. Users based in the United Kingdom or European Union contract with Livewello Ltd as their data controller; all other users contract with Livewello LLC. By using our service, you consent to the data practices described in this policy.
          </Text>
          <Box bg="green.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="green.700" mb={2}>
              🧬 Genetic Data Pledge
            </Text>
            <Text fontSize="md" lineHeight="tall">
              <strong>You own your genetic data. Livewello is a custodian only.</strong> We will never sell, rent, or otherwise commercialize your genetic data, including in de-identified or aggregated form. This commitment binds Livewello and any successor to our business.
            </Text>
          </Box>
        </Box>

        {/* 2. Information We Collect */}
        <Box id="information-collected" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>2. Information We Collect</Heading>
          
          <Text mb={4} fontWeight="700" fontSize="lg">2.1 Personal Information</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Account Information:</strong> Name, email address, phone number (optional)</Box>
            <Box as="li"><strong>Authentication:</strong> Account credentials managed by Firebase Auth</Box>
            <Box as="li"><strong>Billing Information:</strong> Payment data processed by Stripe (we don't store card details)</Box>
            <Box as="li"><strong>Demographics:</strong> Age, gender, ethnicity (optional, for personalized insights)</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">2.2 Genetic Data</Text>
          <Text mb={4} fontSize="md" lineHeight="tall">
            Genetic data is handled under a separate Consent to Use of Your Genetic Data, which we
            ask you to accept on its own before you upload genetic data or register a DNA kit.
            Accepting this Privacy Policy does not by itself give consent to genetic data
            processing, and you can withdraw that consent at any time without closing your account.
          </Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Raw Genetic Files:</strong> Livewello DNA kit results, 23andMe, AncestryDNA, VCF formats, and other genetic testing data</Box>
            <Box as="li"><strong>Processed Variants:</strong> SNPs, genotypes, alleles, and genetic markers</Box>
            <Box as="li"><strong>Analysis Results:</strong> Genetic reports, ancestry estimates, and trait-related insights</Box>
            <Box as="li"><strong>Active File Designation:</strong> One genetic file per profile marked as active for analysis</Box>
            <Box as="li"><strong>Validation:</strong> All genetic files in a profile must belong to the same individual</Box>
          </Box>

          <Box bg="red.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="red.700" mb={2}>
              🚫 Genetic Data Ownership
            </Text>
            <Text fontSize="md" lineHeight="tall">
              <strong>You own your genetic data. We will never sell, rent, or otherwise commercialize your genetic data, including in de-identified or aggregated form. This commitment binds Livewello and any successor to our business.</strong> We are custodians only, processing your data solely for service delivery.
            </Text>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">2.3 Health & Wellness Information</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Health Records:</strong> Medical history, lab results, test data</Box>
            <Box as="li"><strong>Medications & Supplements:</strong> Current and historical medication lists</Box>
            <Box as="li"><strong>Wellness Goals:</strong> Health objectives, progress tracking, accountability data</Box>
            <Box as="li"><strong>Document Uploads:</strong> PDFs, images, medical records, lab reports</Box>
            <Box as="li"><strong>Check-in Media:</strong> Photos, videos, voice recordings for wellness tracking</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">2.4 AI Interaction Data</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Wellness Coach Conversations:</strong> Messages, questions, and AI responses</Box>
            <Box as="li"><strong>AI Function Calls:</strong> Document analysis, genetic insights, health recommendations</Box>
            <Box as="li"><strong>Token Usage:</strong> AI consumption tracking and billing data</Box>
            <Box as="li"><strong>Search Queries:</strong> Health questions and wellness inquiries</Box>
            <Box as="li"><strong>External AI Integration Activity:</strong> MCP API key usage, OAuth connection events, connected app identifiers, and request logs for external AI assistants you connect to your account</Box>
          </Box>

          <Box bg="blue.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="blue.700" mb={2}>
              Document uploads vs. Wellness Coach
            </Text>
            <Text fontSize="md" lineHeight="tall">
              When you upload health documents, we automatically redact direct identifiers (names, SSN, MRN, DOB, addresses, phone numbers, emails) before AI analysis of those documents. When you use Wellness Coach, your messages and the health, genetic, and goal data needed to answer you are sent to our AI providers (OpenAI and Anthropic) without additional redaction. Those providers are contractually prohibited from using your data to train their public models.
            </Text>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">2.5 Usage & Analytics Data</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Session Data:</strong> Login times, IP addresses, user agents, device information</Box>
            <Box as="li"><strong>Feature Usage:</strong> Navigation patterns, tool usage, interaction analytics</Box>
            <Box as="li"><strong>Storage Usage:</strong> File upload activity, storage consumption</Box>
            <Box as="li"><strong>Performance Metrics:</strong> Error logs, response times, system performance</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">2.6 Profile Sharing Data</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Sharing Permissions:</strong> Role-based access (Owner, Editor, Viewer, Restricted Viewer)</Box>
            <Box as="li"><strong>Collaboration Activity:</strong> Sharing events, access logs, permission changes</Box>
            <Box as="li"><strong>Magic Links:</strong> Temporary emergency access links with expiration</Box>
            <Box as="li"><strong>Professional Relationships:</strong> Client-practitioner relationships</Box>
          </Box>
        </Box>

        {/* 3. How We Use Information */}
        <Box id="how-we-use" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>3. How We Use Information</Heading>
          
          <Text mb={4} fontWeight="700" fontSize="lg">3.1 Service Delivery</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Genetic Analysis:</strong> Processing genetic data for personalized insights and reports</Box>
            <Box as="li"><strong>AI Health Coaching:</strong> Wellness Coach conversations powered by OpenAI GPT-4 and Anthropic Claude</Box>
            <Box as="li"><strong>Health Data Management:</strong> Organizing, storing, and analyzing health records and documents</Box>
            <Box as="li"><strong>Wellness Tracking:</strong> Goal setting, progress monitoring, and accountability features</Box>
            <Box as="li"><strong>Profile Sharing:</strong> Enabling secure collaboration with family, healthcare providers, and coaches</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">3.2 AI Processing</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>OpenAI GPT-4:</strong> Primary AI model for Wellness Coach conversations and document analysis</Box>
            <Box as="li"><strong>Anthropic Claude:</strong> Alternative AI model for conversations and health insights</Box>
            <Box as="li"><strong>Google Cloud Vision:</strong> Image analysis for check-in media and document processing</Box>
            <Box as="li"><strong>PII Redaction:</strong> Automatic removal of personally identifiable information before AI processing</Box>
            <Box as="li"><strong>No Training:</strong> Your data is NOT used to train AI models (per OpenAI/Anthropic agreements)</Box>
            <Box as="li"><strong>External AI Integrations:</strong> When you connect a third-party AI assistant via MCP, your data is transmitted to that service at your request. See Section 5 for details on how external AI integrations handle your data</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">3.3 Subscription Management</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Billing & Payments:</strong> Processing subscriptions and payments via Stripe</Box>
            <Box as="li"><strong>Usage Tracking:</strong> Monitoring storage, tokens, and profile limits</Box>
            <Box as="li"><strong>Plan Enforcement:</strong> Managing subscription tiers and feature access</Box>
            <Box as="li"><strong>Overage Detection:</strong> Identifying usage exceeding plan limits</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">3.4 Communications</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Transactional Emails:</strong> Account creation, password reset, payment receipts</Box>
            <Box as="li"><strong>Service Notifications:</strong> Usage warnings, expiry alerts, system updates</Box>
            <Box as="li"><strong>Marketing Communications:</strong> Only with consent, opt-out available</Box>
            <Box as="li"><strong>Support Communications:</strong> Customer service and technical support</Box>
            <Box as="li"><strong>SMS Messages:</strong> Wellness goal reminders, coaching check-ins, account notifications, and one-time verification codes, sent only to users who have explicitly opted in. Message frequency varies based on your wellness activity (typically 1–5 messages per week). Message and data rates may apply. Reply STOP to cancel or HELP for help at any time.</Box>
            <Box as="li"><strong>WhatsApp Messages:</strong> Wellness goal reminders, coaching notifications, and account updates delivered via WhatsApp Business, sent only to users who have explicitly opted in. WhatsApp messages are processed by Meta Platforms, Inc. in accordance with WhatsApp's Privacy Policy. You may opt out at any time by replying STOP.</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">3.5 Legal & Compliance</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Fraud Prevention:</strong> Security monitoring and abuse detection</Box>
            <Box as="li"><strong>Legal Obligations:</strong> Compliance with laws and regulatory requirements</Box>
            <Box as="li"><strong>Terms Enforcement:</strong> Ensuring compliance with Terms of Service</Box>
            <Box as="li"><strong>Audit Trails:</strong> Maintaining records for compliance and security</Box>
          </Box>
        </Box>

        {/* 4. Information Sharing & Disclosure */}
        <Box id="information-sharing" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>4. Information Sharing & Disclosure</Heading>
          
          <Text mb={4} fontWeight="700" fontSize="lg">4.1 User-Initiated Sharing</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Profile Sharing:</strong> You control who can access your profiles and data</Box>
            <Box as="li"><strong>Role-Based Access:</strong> Owner, Editor, Viewer, Restricted Viewer permissions</Box>
            <Box as="li"><strong>Professional Sharing:</strong> Users managing client profiles</Box>
            <Box as="li"><strong>Goals Partners:</strong> Wellness goal sharing with trusted individuals</Box>
            <Box as="li"><strong>Magic Links:</strong> Temporary emergency access with automatic expiration</Box>
          </Box>

          <Box bg="green.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="green.700" mb={2}>
              ✅ Your Control
            </Text>
            <Text fontSize="md" lineHeight="tall">
              <strong>We only share your data with others when YOU explicitly grant access, or with the service providers listed in Section 4.2 who help us operate and market Livewello.</strong> You can revoke user-initiated sharing at any time through your account settings, and can opt out of the advertising use described in Section 4.2 at any time.
            </Text>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">4.2 Service Providers (Data Processors)</Text>
          
          <Text mb={3} fontWeight="600" fontSize="md">Infrastructure:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Google Cloud Platform:</strong> Backend compute, data storage (Firestore, BigQuery, Cloud Storage), and supporting infrastructure</Box>
            <Box as="li"><strong>Firebase:</strong> Authentication, Firestore database access, Cloud Storage access, and frontend hosting</Box>
          </Box>

          <Text mb={3} fontWeight="600" fontSize="md">AI Services:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>OpenAI:</strong> Language models and vision analysis for Wellness Coach and document processing</Box>
            <Box as="li"><strong>Anthropic:</strong> Language models for Wellness Coach</Box>
            <Box as="li"><strong>Pinecone:</strong> Vector search for document retrieval and genetic knowledge references; may store embeddings derived from your uploaded documents</Box>
          </Box>

          <Text mb={3} fontWeight="600" fontSize="md">Business Services:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Stripe:</strong> Payment processing (PCI-compliant, we don't store card data)</Box>
            <Box as="li"><strong>SendGrid:</strong> Email delivery service</Box>
            <Box as="li"><strong>Twilio:</strong> SMS and WhatsApp message delivery. Your phone number is transmitted to Twilio solely to send messages you have opted in to receive. Twilio is bound by a data processing agreement prohibiting unauthorized use.</Box>
            <Box as="li"><strong>Meta Platforms (WhatsApp Business):</strong> WhatsApp message delivery for users who opt in to WhatsApp notifications. Messages are processed by Meta in accordance with the WhatsApp Business Terms of Service and Meta Privacy Policy.</Box>
            <Box as="li"><strong>Advertising Platforms:</strong> We may share your hashed email address with advertising platforms (currently Meta) to build lookalike or custom advertising audiences — matching against the platform's own users to reach people with similar characteristics, for Livewello ad campaigns. Your email is hashed (SHA-256) before it leaves our systems; the platform cannot recover your plaintext email from the hash. When you arrive from one of our ads and make a purchase, we may also send that platform the ad click identifier, your IP address and browser type so it can measure the ad's performance. This is separate from the on-site advertising cookies described in Section 9.2. You can opt out at any time by contacting support@livewello.com, and we will not include your email in future audience uploads.</Box>
          </Box>

          <Box bg="blue.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="blue.700" mb={2}>
              📋 Data Processing Agreements
            </Text>
            <Text fontSize="md" lineHeight="tall">
              All service providers are bound by data processing agreements requiring appropriate security and privacy safeguards (including GDPR where applicable) and prohibiting unauthorized use of your data.
            </Text>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">4.3 Legal Requirements</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Law Enforcement:</strong> Court orders, subpoenas, search warrants</Box>
            <Box as="li"><strong>Legal Compliance:</strong> Regulatory investigations, audits</Box>
            <Box as="li"><strong>Business Transfers:</strong> Mergers or acquisitions (with notice). Any successor remains bound by our genetic data pledge</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">4.3.1 Safety Exceptions</Text>
          <Text mb={3} fontSize="md" lineHeight="tall">
            In limited circumstances, we may disclose your health information without your authorization to prevent serious harm:
          </Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Danger to Self:</strong> If we have reason to believe you pose a serious and imminent threat of harm to yourself (including suicide or self-harm), we may disclose relevant health information to emergency responders, mental health professionals, or other appropriate parties to help prevent harm.</Box>
            <Box as="li"><strong>Danger to Others:</strong> If we have reason to believe you pose a serious and imminent threat of physical harm to another person, we may disclose relevant health information to law enforcement, potential victims, or other appropriate parties to help prevent harm.</Box>
            <Box as="li"><strong>Child or Vulnerable Adult Protection:</strong> If we have reason to believe a child or vulnerable adult is at risk of abuse, neglect, or harm, we may disclose relevant information to child protective services or adult protective services as required by law.</Box>
            <Box as="li"><strong>Public Health Threats:</strong> We may disclose information to public health authorities when necessary to prevent or control disease, injury, or disability, or to report suspected abuse, neglect, or domestic violence as required by law.</Box>
          </Box>

          <Box bg="orange.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="orange.700" mb={2}>
              ⚠️ Safety Exception Notice
            </Text>
            <Text fontSize="md" lineHeight="tall" mb={2}>
              <strong>These safety exceptions are used only in serious situations where there is a clear and imminent risk of harm.</strong> We will:
            </Text>
            <Box as="ul" gap={1} pl={4} fontSize="md" lineHeight="tall">
              <Box as="li">Disclose only the minimum necessary information to address the safety concern</Box>
              <Box as="li">Notify you of the disclosure when legally permitted and safe to do so</Box>
              <Box as="li">Document safety-related disclosures as required by applicable law</Box>
            </Box>
          </Box>

          <Box bg="yellow.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="yellow.700" mb={2}>
              ⚖️ Transparency Commitment
            </Text>
            <Text fontSize="md" lineHeight="tall">
              <strong>We will notify you of legal requests for your data unless prohibited by law or when notification would compromise safety or law enforcement efforts.</strong>
            </Text>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">4.4 De-Identified Data</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Aggregated Statistics:</strong> Usage trends, feature adoption (no personal identifiers)</Box>
            <Box as="li"><strong>Research:</strong> De-identified genetic insights (only with explicit consent)</Box>
            <Box as="li"><strong>Product Improvement:</strong> Anonymized data for feature development</Box>
          </Box>

          <Box bg="red.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="red.700" mb={2}>
              🧬 Genetic Data Pledge
            </Text>
            <Text fontSize="md" lineHeight="tall">
              <strong>We will never sell, rent, or otherwise commercialize your genetic data, including in de-identified or aggregated form. This commitment binds Livewello and any successor to our business.</strong>
            </Text>
          </Box>
        </Box>

        {/* 5. External AI Integrations (MCP) */}
        <Box id="external-ai-integrations" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>5. External AI Integrations (MCP)</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">
            LiveWello supports the Model Context Protocol (MCP), allowing you to connect third-party AI assistants — such as Claude Desktop, ChatGPT, or other compatible clients — to your LiveWello account. This section explains how your data is handled when you use these integrations.
          </Text>

          <Text mb={4} fontWeight="700" fontSize="lg">5.1 What Data Is Shared</Text>
          <Text mb={3} fontSize="md" lineHeight="tall">
            When you connect an external AI assistant and grant it access, the following data may be transmitted to that third-party service based on the scopes you authorize:
          </Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">Profile information (yours and any shared profiles you can access)</Box>
            <Box as="li">Health records, lab results, and medical history</Box>
            <Box as="li">Genetic summaries, SNP data, and genotype information</Box>
            <Box as="li">Wellness goals, progress data, and check-in history</Box>
            <Box as="li">Uploaded documents and their content</Box>
            <Box as="li">Account preferences and session context</Box>
          </Box>

          <Box bg="orange.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="orange.700" mb={2}>
              Important: PII Redaction Does Not Apply to MCP
            </Text>
            <Text fontSize="md" lineHeight="tall">
              The automatic PII redaction system that protects your data during Wellness Coach conversations <strong>does not apply</strong> to data accessed by external AI assistants via MCP. When you grant an external AI assistant access, it receives your data as stored in LiveWello, including personally identifiable information.
            </Text>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">5.2 How Sharing Is Controlled</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>User-Initiated Only:</strong> External AI assistants can only access your data after you explicitly approve a connection via OAuth consent or generate an API key</Box>
            <Box as="li"><strong>Scoped Access:</strong> You choose whether to grant read-only (<em>livewello:read</em>) or read-write (<em>livewello:write</em>) access</Box>
            <Box as="li"><strong>Revocable:</strong> You can disconnect any external AI assistant at any time from Account Settings → Integrations</Box>
            <Box as="li"><strong>API Key Management:</strong> You can create, rotate, and delete MCP API keys at any time</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">5.3 Third-Party Data Handling</Text>
          <Text mb={3} fontSize="md" lineHeight="tall">
            Once your data is transmitted to an external AI assistant, that service's own privacy policy and terms of service govern how your data is processed, stored, and used. Specifically:
          </Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>LiveWello's control ends at transmission:</strong> We transmit your data to the external AI assistant in response to authenticated requests, but we do not control what happens to it afterward</Box>
            <Box as="li"><strong>No guarantee of PII protection:</strong> External AI assistants may not redact, encrypt, or protect your data to the same standards as LiveWello</Box>
            <Box as="li"><strong>Training policies vary:</strong> Some AI providers may use data sent to them for model training unless you opt out under their policies</Box>
            <Box as="li"><strong>Review before connecting:</strong> We strongly recommend reviewing the privacy policy of any AI assistant before granting it access to your LiveWello data</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">5.4 What We Log</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">Which external AI assistants are connected to your account</Box>
            <Box as="li">When connections are created, used, and revoked</Box>
            <Box as="li">Request volume and rate-limit events (for security and abuse prevention)</Box>
            <Box as="li">We log connection metadata (which app, when, request volume). We do not log the content of successful data transfers; truncated error diagnostics may be retained briefly for security and reliability</Box>
          </Box>

          <Box bg="green.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="green.700" mb={2}>
              Your Control Over External AI Access
            </Text>
            <Text fontSize="md" lineHeight="tall">
              You are always in control. You decide which AI assistants to connect, what level of access to grant, and can revoke access instantly. We recommend periodically reviewing your connected apps in Account Settings → Integrations.
            </Text>
          </Box>
        </Box>

        {/* 6. Data Security */}
        <Box id="data-security" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>6. Data Security</Heading>
          
          <Text mb={4} fontWeight="700" fontSize="lg">6.1 Technical Safeguards</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Encryption:</strong> Encryption for sensitive data in transit and at rest</Box>
            <Box as="li"><strong>PII Redaction:</strong> Automatic redaction of direct identifiers from uploaded health documents before AI analysis (Wellness Coach uses broader context as described in Section 2.4)</Box>
            <Box as="li"><strong>Access Controls:</strong> Role-based permissions and multi-factor authentication</Box>
            <Box as="li"><strong>Audit Logs:</strong> Complete trail of data access and modifications</Box>
            <Box as="li"><strong>Secure Infrastructure:</strong> Google Cloud Platform with industry-standard security controls</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">6.2 Administrative Safeguards</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Employee Training:</strong> Privacy and security best practices</Box>
            <Box as="li"><strong>Access Restrictions:</strong> Minimum necessary access principle</Box>
            <Box as="li"><strong>Incident Response:</strong> 72-hour breach notification procedures</Box>
            <Box as="li"><strong>Vendor Management:</strong> Regular security assessments of service providers</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">6.3 Physical Safeguards</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Data Centers:</strong> Google Cloud data centers with physical and environmental controls</Box>
            <Box as="li"><strong>Redundancy:</strong> Multi-region backups and disaster recovery</Box>
            <Box as="li"><strong>Physical Security:</strong> Controlled access to infrastructure</Box>
          </Box>
        </Box>

        {/* 7. Data Retention & Deletion */}
        <Box id="data-retention" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>7. Data Retention & Deletion</Heading>
          
          <Text mb={4} fontWeight="700" fontSize="lg">7.1 Retention Periods</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Active Accounts:</strong> Data retained indefinitely while account is active</Box>
            <Box as="li"><strong>Deleted Accounts:</strong> 30-day grace period after you request deletion, then permanent deletion</Box>
            <Box as="li"><strong>Downgraded Accounts:</strong> 90 days grace period for excess data</Box>
            <Box as="li"><strong>Legacy Migrated Accounts:</strong> 1 year retention of Legacy data post-migration</Box>
            <Box as="li"><strong>AI Conversations:</strong> Retained per user settings (can be deleted anytime)</Box>
            <Box as="li"><strong>Audit Logs:</strong> 7 years for compliance (de-identified after account deletion)</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">7.2 Deletion Rights</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Account Deletion:</strong> Permanent deletion of all data within 30 days</Box>
            <Box as="li"><strong>Profile Deletion:</strong> Immediate deletion of profile and subcollections</Box>
            <Box as="li"><strong>Genetic Data Deletion:</strong> Permanent removal from all systems (Firestore, BigQuery, GCS)</Box>
            <Box as="li"><strong>External Data Cleanup:</strong> Automatic deletion from Pinecone, Redis, GCS</Box>
            <Box as="li"><strong>Right to Be Forgotten:</strong> GDPR-compliant deletion process</Box>
          </Box>

          <Box bg="blue.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="blue.700" mb={2}>
              🗑️ Deletion Process
            </Text>
            <Box as="ol" gap={2} pl={4} fontSize="md" lineHeight="tall">
              <Box as="li">User initiates deletion via Account Settings</Box>
              <Box as="li">30-day grace period (can cancel deletion)</Box>
              <Box as="li">After 30 days: Permanent deletion from all systems</Box>
              <Box as="li">Confirmation email sent upon completion</Box>
            </Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">What Gets Deleted:</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">All profiles and genetic data</Box>
            <Box as="li">Health records and documents</Box>
            <Box as="li">AI conversations and messages</Box>
            <Box as="li">Billing history and payment records</Box>
            <Box as="li">Usage logs and analytics data</Box>
            <Box as="li">External data (GCS files, BigQuery tables, Pinecone vectors)</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">What's Retained (De-Identified):</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li">Audit logs (7 years, PII removed)</Box>
            <Box as="li">Aggregated statistics (no personal identifiers)</Box>
            <Box as="li">Legal compliance records (as required by law)</Box>
          </Box>
        </Box>

        {/* 8. Your Rights */}
        <Box id="your-rights" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>8. Your Rights</Heading>
          
          <Text mb={4} fontWeight="700" fontSize="lg">8.1 General Rights (All Users)</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Access:</strong> View and download all your data</Box>
            <Box as="li"><strong>Correction:</strong> Update inaccurate information</Box>
            <Box as="li"><strong>Deletion:</strong> Permanently delete your account and data</Box>
            <Box as="li"><strong>Portability:</strong> Export data as a ZIP archive containing JSON and original uploaded files (including genetic files where applicable)</Box>
            <Box as="li"><strong>Objection:</strong> Opt-out of marketing communications</Box>
            <Box as="li"><strong>Restriction:</strong> Limit processing of your data</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">8.2 Genetic Data Rights</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Ownership:</strong> You own all genetic data</Box>
            <Box as="li"><strong>Export:</strong> Download raw files and analysis results</Box>
            <Box as="li"><strong>Deletion:</strong> Permanent removal from all systems</Box>
            <Box as="li"><strong>Sample Destruction:</strong> For DNA kit customers, your biological sample is destroyed by our laboratory partner after analysis is complete. You may also request destruction sooner, at any time, by contacting support@livewello.com, and we will confirm within 10 business days. Destroying your sample does not delete genetic data already produced from it — use the deletion right above for that. Our laboratory may separately retain de-identified material for internal quality validation, as described in the consent form you sign when registering your kit</Box>
            <Box as="li"><strong>No Commercialization:</strong> We will never sell, rent, or otherwise commercialize your genetic data, including in de-identified or aggregated form — and any successor to our business is bound by the same commitment</Box>
            <Box as="li"><strong>Research Consent:</strong> Explicit opt-in required for research use</Box>
            <Box as="li"><strong>Withdraw Genetic Consent:</strong> Withdraw your consent for genetic data at any time in Settings. Because that consent is what allows us to hold and process your genetic data, withdrawing it permanently deletes your genetic files, analysis results, and reports across all profiles. Your account, profiles, and health records remain</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">8.3 GDPR Rights (EU Users)</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Right of Access (Article 15):</strong> Request copy of your data</Box>
            <Box as="li"><strong>Right of Rectification (Article 16):</strong> Correct inaccurate data</Box>
            <Box as="li"><strong>Right of Erasure (Article 17):</strong> "Right to be forgotten"</Box>
            <Box as="li"><strong>Right of Portability (Article 20):</strong> Receive data in machine-readable format</Box>
            <Box as="li"><strong>Right to Object (Article 21):</strong> Object to data processing</Box>
            <Box as="li"><strong>Right to Restrict Processing (Article 18):</strong> Limit how data is used</Box>
            <Box as="li"><strong>Right to Lodge Complaint:</strong> Contact supervisory authority</Box>
          </Box>

          <Box bg="green.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="green.700" mb={2}>
              ⚖️ GDPR Legal Basis
            </Text>
            <Box as="ul" gap={2} pl={4} fontSize="md" lineHeight="tall">
              <Box as="li"><strong>Consent:</strong> For marketing and optional features</Box>
              <Box as="li"><strong>Contract Performance:</strong> For service delivery</Box>
              <Box as="li"><strong>Legitimate Interest:</strong> For fraud prevention and security</Box>
              <Box as="li"><strong>Legal Obligation:</strong> For compliance with laws</Box>
            </Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">8.4 US Privacy Rights</Text>
          <Text mb={3} fontSize="md" lineHeight="tall">
            Livewello is a direct-to-consumer wellness platform, not a healthcare provider or health plan. Your health and genetic information is protected under this Privacy Policy and applicable state and federal privacy laws — not the HIPAA Privacy Rule, unless you separately enter a covered-entity arrangement with us when such features are offered.
          </Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Access &amp; Correction:</strong> Request a copy of or corrections to personal information we hold</Box>
            <Box as="li"><strong>Deletion:</strong> Request account or data deletion as described in Section 7</Box>
            <Box as="li"><strong>State Privacy Laws:</strong> Residents of California, Virginia, Colorado, and other states with comprehensive privacy laws may have additional rights — contact us to exercise them</Box>
            <Box as="li"><strong>Genetic Privacy:</strong> Where applicable (e.g. GINA, state genetic privacy laws), protections against discrimination based on genetic information</Box>
          </Box>

          <Box bg="blue.50" p={4} borderRadius="md" mb={4}>
            <Text fontWeight="700" fontSize="lg" color="blue.700" mb={2}>
              📞 How to Exercise Rights
            </Text>
            <Box as="ul" gap={2} pl={4} fontSize="md" lineHeight="tall">
              <Box as="li"><strong>Email:</strong> support@livewello.com</Box>
              <Box as="li"><strong>Account Settings:</strong> Data export and deletion tools</Box>
              <Box as="li"><strong>Support:</strong> Contact support for assistance</Box>
            </Box>
          </Box>
        </Box>

        {/* 9. Cookies & Tracking */}
        <Box id="cookies-tracking" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>9. Cookies & Tracking</Heading>
          
          <Text mb={4} fontSize="md" lineHeight="tall">
            We use essential cookies for authentication and security inside the app. On our public marketing pages only, we may use Google advertising cookies to measure ad performance if you opt in through the cookie banner. We do not use advertising or analytics cookies inside the logged-in app.
          </Text>

          <Text mb={4} fontWeight="700" fontSize="lg">9.1 Essential Cookies</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Authentication:</strong> Firebase Auth session cookies</Box>
            <Box as="li"><strong>Security:</strong> CSRF protection tokens</Box>
            <Box as="li"><strong>Session Management:</strong> User preferences and state</Box>
          </Box>

          <Text mb={4} fontSize="md" lineHeight="tall">
            Essential cookies are required for core functionality and cannot be disabled while using the service.
          </Text>

          <Text mb={4} fontWeight="700" fontSize="lg">9.2 Marketing Cookies (public pages only)</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Google Ads:</strong> Conversion measurement on public marketing pages, loaded only after you opt in</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">9.3 Third-Party Cookies</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Stripe:</strong> Payment processing (required for checkout)</Box>
            <Box as="li"><strong>Google Cloud:</strong> Infrastructure, hosting, and CDN</Box>
          </Box>
        </Box>

        {/* 10. International Transfers */}
        <Box id="international-transfers" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>10. International Transfers</Heading>
          
          <Text mb={4} fontWeight="700" fontSize="lg">10.1 Data Storage Location</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Primary:</strong> United States (Google Cloud US regions)</Box>
            <Box as="li"><strong>Backups:</strong> Multi-region for disaster recovery</Box>
            <Box as="li"><strong>CDN:</strong> Global distribution via Firebase Hosting and Google Cloud infrastructure</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">10.2 EU-US Data Transfers</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Legal Mechanism:</strong> Standard Contractual Clauses (SCCs)</Box>
            <Box as="li"><strong>Adequacy:</strong> Compliance with GDPR Article 46</Box>
            <Box as="li"><strong>Safeguards:</strong> Encryption, access controls, audit trails</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">10.3 AI Service Providers</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>OpenAI:</strong> US-based, PII-redacted data only</Box>
            <Box as="li"><strong>Anthropic:</strong> US-based, PII-redacted data only</Box>
            <Box as="li"><strong>Data Minimization:</strong> Only necessary data sent to AI services</Box>
          </Box>
        </Box>

        {/* 11. Children's Privacy */}
        <Box id="children-privacy" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>11. Children's Privacy</Heading>
          
          <Text mb={4} fontWeight="700" fontSize="lg">11.1 Age Requirements</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Minimum Age:</strong> You must be at least 18 years old (or the age of majority where you live) to create a Livewello account</Box>
            <Box as="li"><strong>Family Profiles:</strong> An adult account holder may create profiles for family members, including minors, and is responsible for having legal authority to upload and manage that person&apos;s health and genetic information</Box>
            <Box as="li"><strong>COPPA:</strong> We do not knowingly allow children under 13 to hold their own accounts</Box>
            <Box as="li"><strong>Authority Attestation:</strong> When you create a profile for another person under 18, we ask you to confirm you have legal authority to manage their health and genetic information. We record that confirmation</Box>
            <Box as="li"><strong>Reaching Adulthood:</strong> When a minor whose profile you manage reaches the age of majority, they may request transfer of their profile to their own account, or deletion of their data, by contacting support@livewello.com</Box>
          </Box>
        </Box>

        {/* 12. Changes to Privacy Policy */}
        <Box id="policy-changes" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>12. Changes to Privacy Policy</Heading>
          
          <Text mb={4} fontWeight="700" fontSize="lg">12.1 Notification Process</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Material Changes:</strong> 30 days notice via email</Box>
            <Box as="li"><strong>Version Control:</strong> Version number and effective date</Box>
            <Box as="li"><strong>Acceptance:</strong> Continued use constitutes acceptance</Box>
            <Box as="li"><strong>Right to Terminate:</strong> Cancel account if you disagree</Box>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg">12.2 Change History</Text>
          <Box as="ul" gap={2} pl={6} mb={4} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>Archive:</strong> Previous versions at <Link href="/privacy/archive" color="blue.500">livewello.com/privacy/archive</Link> with summaries of major changes</Box>
            <Box as="li"><strong>Notification Banner:</strong> In-app alert when a new version requires your acceptance</Box>
          </Box>
        </Box>

        {/* 13. Contact Information */}
        <Box id="contact" mt={sectionMt}>
          <Heading size={headingSize} fontWeight="700" mb={compact ? 3 : 6}>13. Contact Information</Heading>
          <Text mb={4} fontSize="md" lineHeight="tall">How to reach us for privacy-related questions:</Text>
          
          <Box bg="gray.50" p={4} borderRadius="md">
            <Text mb={2} fontSize="md" lineHeight="tall"><strong>Contact:</strong> <Link href="mailto:support@livewello.com" color="blue.500">support@livewello.com</Link></Text>
            <Text mb={2} fontSize="md" lineHeight="tall"><strong>Website:</strong> <Link href="https://livewello.com/privacy" color="blue.500" target="_blank" rel="noopener noreferrer">https://livewello.com/privacy <FiExternalLink style={{ marginLeft: '2px' }} /></Link></Text>
          </Box>

          <Text mb={4} fontWeight="700" fontSize="lg" mt={6}>Regulatory Contacts</Text>
          <Box as="ul" gap={2} pl={6} fontSize="md" lineHeight="tall">
            <Box as="li"><strong>GDPR Complaints:</strong> EU/UK Data Protection Authorities</Box>
            <Box as="li"><strong>FTC Complaints:</strong> US Federal Trade Commission (unfair or deceptive practices)</Box>
            <Box as="li"><strong>State Attorneys General:</strong> For state privacy law concerns in your jurisdiction</Box>
          </Box>
        </Box>

        {!compact && <Box height="1px" bg="gray.200" my={6} />}

        <Box textAlign="center" color="gray.600" fontSize={compact ? 'sm' : 'md'} mt={compact ? 4 : 12}>
          <Text>
            By using LiveWello, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
          </Text>
        </Box>
      </VStack>
    </Box>
  )
}

export default PrivacyPolicyContent
